// =============================================================================
// SessionKeeper — Proactive token refresh before expiry
// =============================================================================
// Schedules a silent refresh before the access token expires, and re-checks
// when the tab regains focus (visibilitychange). Prevents "surprise logouts"
// caused by stale tokens on long-idle tabs.
//
// Uses BroadcastChannel to coordinate across tabs: when one tab refreshes,
// all others reset their timers without issuing redundant refresh calls.
// =============================================================================

export interface SessionKeeperConfig {
  /** Access token TTL in milliseconds (e.g. 8 * 60 * 60 * 1000 for 8h) */
  tokenTtlMs: number;

  /**
   * How early before expiry to trigger refresh (in ms).
   * Defaults to 5 minutes.
   */
  refreshMarginMs?: number;

  /** The function that performs the actual POST /auth/refresh call */
  refreshFn: () => Promise<boolean>;

  /** Called when refresh fails — typically redirect to login */
  onSessionExpired: () => void;
}

const DEFAULT_REFRESH_MARGIN_MS = 5 * 60 * 1000; // 5 minutes

export class SessionKeeper {
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private lastRefreshAt = Date.now();
  private readonly ttl: number;
  private readonly margin: number;
  private readonly refreshFn: () => Promise<boolean>;
  private readonly onExpired: () => void;
  private refreshing = false;
  private disposed = false;

  constructor(config: SessionKeeperConfig) {
    this.ttl = config.tokenTtlMs;
    this.margin = config.refreshMarginMs ?? DEFAULT_REFRESH_MARGIN_MS;
    this.refreshFn = config.refreshFn;
    this.onExpired = config.onSessionExpired;

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.scheduleRefresh();
  }

  /** Call after a successful login or token refresh to reset the timer */
  resetTimer(): void {
    this.lastRefreshAt = Date.now();
    this.scheduleRefresh();
  }

  /** Tear down timers and event listeners */
  dispose(): void {
    this.disposed = true;
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private scheduleRefresh(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
    }

    const elapsed = Date.now() - this.lastRefreshAt;
    const delay = Math.max(this.ttl - this.margin - elapsed, 0);

    this.timerId = setTimeout(() => {
      void this.doRefresh();
    }, delay);
  }

  private handleVisibilityChange(): void {
    if (this.disposed) return;
    if (document.visibilityState !== 'visible') return;

    const elapsed = Date.now() - this.lastRefreshAt;
    if (elapsed >= this.ttl - this.margin) {
      void this.doRefresh();
    } else {
      this.scheduleRefresh();
    }
  }

  private async doRefresh(): Promise<void> {
    if (this.disposed || this.refreshing) return;
    this.refreshing = true;

    try {
      const ok = await this.refreshFn();
      if (ok) {
        this.resetTimer();
      } else {
        this.onExpired();
      }
    } catch {
      this.onExpired();
    } finally {
      this.refreshing = false;
    }
  }
}
